//#region src/sam-client.ts
var SamClient = class {
	constructor(proxyPath, fetchFn, options) {
		this.proxyPath = void 0;
		this.fetchFn = void 0;
		this.initPromise = null;
		this.confidenceThreshold = void 0;
		this.sessionId = void 0;
		this.preferredModel = "sam2_l";
		this.proxyPath = proxyPath;
		this.fetchFn = fetchFn ?? globalThis.fetch.bind(globalThis);
		this.confidenceThreshold = options?.confidenceThreshold ?? .5;
		this.sessionId = crypto.randomUUID();
	}
	setConfidenceThreshold(value) {
		this.confidenceThreshold = value;
	}
	/** Probe server availability and discover models. */
	async init() {
		if (!this.initPromise) this.initPromise = this.probeServer();
		return this.initPromise;
	}
	async probeServer() {
		try {
			const res = await this.fetchFn(`${this.proxyPath}/models`);
			if (!res.ok) return null;
			const data = await res.json();
			if (data.models.includes("sam3")) this.preferredModel = "sam3";
			return { available: true };
		} catch {
			return null;
		}
	}
	async predict(params, signal) {
		await this.init();
		let bbox;
		if (params.bbox) {
			const [x1, y1, x2, y2] = params.bbox;
			bbox = [
				Math.round(x1),
				Math.round(y1),
				Math.round(x2 - x1),
				Math.round(y2 - y1)
			];
		}
		const body = JSON.stringify({
			model: params.modelType ?? this.preferredModel,
			image: params.b64img,
			prompts: {
				points: params.pointCoords.length > 0 ? params.pointCoords.map(([x, y]) => [Math.round(x), Math.round(y)]) : null,
				point_labels: params.pointCoords.length > 0 ? params.pointLabels : null,
				bbox: bbox ?? null
			},
			options: {
				multimask_output: params.multimaskOutput ?? false,
				reset_prompts: true,
				confidence_threshold: this.confidenceThreshold
			},
			session_id: this.sessionId
		});
		const res = await this.fetchFn(`${this.proxyPath}/predict`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body,
			signal
		});
		if (!res.ok) {
			const text = await res.text().catch(() => "");
			throw new Error(`SAM predict failed (${res.status}): ${text}`);
		}
		const data = await res.json();
		let features = data.masks;
		if (features.length > 1 && params.pointCoords.length > 0) {
			const [px, py] = params.pointCoords[0];
			const containing = features.filter((f) => {
				const ring = f.geometry.coordinates[0];
				if (!ring || ring.length < 3) return false;
				return pointInPolygon(px, py, ring);
			});
			if (containing.length > 0) features = containing;
			else {
				const bboxContaining = features.filter((f) => {
					const ring = f.geometry.coordinates[0];
					if (!ring || ring.length < 3) return false;
					return polygonBboxContains(px, py, ring);
				});
				if (bboxContaining.length > 0) features = bboxContaining;
			}
		}
		return features.map((feature) => {
			const ring = feature.geometry.coordinates[0] ?? [];
			const points = new Int32Array(ring.length * 2);
			for (let i = 0; i < ring.length; i++) {
				points[i * 2] = Math.round(ring[i][0]);
				points[i * 2 + 1] = Math.round(ring[i][1]);
			}
			return {
				points,
				quality: feature.properties.quality,
				objectIdx: feature.properties.index
			};
		});
	}
};
function pointInPolygon(px, py, ring) {
	let inside = false;
	for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
		const [xi, yi] = ring[i];
		const [xj, yj] = ring[j];
		if (yi > py !== yj > py && px < (xj - xi) * (py - yi) / (yj - yi) + xi) inside = !inside;
	}
	return inside;
}
function polygonBboxContains(px, py, ring) {
	let minX = Infinity;
	let minY = Infinity;
	let maxX = -Infinity;
	let maxY = -Infinity;
	for (const [x, y] of ring) {
		if (x < minX) minX = x;
		if (y < minY) minY = y;
		if (x > maxX) maxX = x;
		if (y > maxY) maxY = y;
	}
	return px >= minX && px <= maxX && py >= minY && py <= maxY;
}

//#endregion
export { SamClient };
//# sourceMappingURL=index.js.map